import { AnalyticsBrowser } from '@segment/analytics-next';

import '../global';
import { IdentifyTraits, OrganizationTraits } from './types';

let segmentBrowser: AnalyticsBrowser | undefined = undefined;

/** The latest page view before Segment loaded, which is after sign-in and consent, so the landing page isn't lost. */
let pendingPage: { name: string; properties?: object } | undefined = undefined;

export interface AnalyticsTrackOptions {
    context?: {
        groupId?: string;
    };
}

/** Call it from `initializeServices`. */
export function initAnalytics(): void {
    if (segmentBrowser) {
        return; // Already initialized
    }
    if (!window.SEGMENT_WRITE_KEY) {
        return;
    }
    segmentBrowser = AnalyticsBrowser.load({
        writeKey: window.SEGMENT_WRITE_KEY,
    });
}

/** Segment-backed tracking; call `initAnalytics()` first. */
export const analytics = {
    /** Only call it once the user has consented to analytics cookies. */
    identify(userId: string, traits: IdentifyTraits): void {
        segmentBrowser?.identify(
            userId,
            {
                email: traits.email,
                language: traits.language,
                user_type: traits.user_type,
                is_from_customer: traits.is_from_customer,
                customer_name: traits.customer_name,
                host: traits.host,
                tenant: traits.tenant,
                has_access_to_customer_portal: traits.has_access_to_customer_portal,
                has_access_to_source_to_contract: traits.has_access_to_source_to_contract,
                is_closed_beta_tester: traits.is_closed_beta_tester,
                has_full_manufacturing_access: traits.has_full_manufacturing_access,
                has_customers: traits.has_customers,
                client_type: traits.client_type,
            },
            { groupId: traits.tenant },
        );

        // Sent after identify, not on load, so the landing page is attributed to the user like every page after.
        if (segmentBrowser && pendingPage) {
            segmentBrowser.page(pendingPage.name, pendingPage.properties);
            pendingPage = undefined;
        }
    },

    /** Segment fills in the user ID if `identify` ran first. */
    group(orgId: string, traits: OrganizationTraits): void {
        segmentBrowser?.group(orgId, {
            name: traits.name,
            has_access_to_customer_portal: traits.has_access_to_customer_portal,
            has_access_to_source_to_contract: traits.has_access_to_source_to_contract,
            is_closed_beta_tester: traits.is_closed_beta_tester,
            has_customers: traits.has_customers,
            client_type: traits.client_type,
        });
    },

    /** No PII in `properties`. Unlike a page view, an event before Segment loads is dropped. */
    track(event: string, properties?: object, options?: AnalyticsTrackOptions): void {
        segmentBrowser?.track(event, properties, options);
    },

    /** Held until the next `identify` while Segment hasn't loaded; see `pendingPage`. */
    page(name: string, properties?: object): void {
        if (!segmentBrowser) {
            // Only the latest: the pages before it were viewed before consent was known.
            pendingPage = { name, properties };
            return;
        }
        segmentBrowser.page(name, properties);
    },
};
